import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/pricing-rules';

/**
 * Map rule_name to frontend activeSource key
 */
const RULE_NAME_TO_SOURCE = {
  'Supplier Buy Price': 'supplierBuyPrice',
  'API Pricing': 'apiPricing',
  'Offline Input': 'offlineInput',
};

const SOURCE_TO_RULE_NAME = {
  supplierBuyPrice: 'Supplier Buy Price',
  apiPricing: 'API Pricing',
  offlineInput: 'Offline Input',
};

/**
 * Map rule_name to frontend strategy key
 */
const RULE_NAME_TO_STRATEGY = {
  'Standard Round': 'round',
  'Always Round Up': 'ceil',
  'Always Round Down': 'floor',
  "Banker's Rounding": 'banker',
};

const STRATEGY_TO_RULE_NAME = {
  round: 'Standard Round',
  ceil: 'Always Round Up',
  floor: 'Always Round Down',
  banker: "Banker's Rounding",
};

/**
 * GET Base Price Calculation Rules
 */
export function usePricingRules() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(BASE);
      const list = res?.base_price_calculation_config ?? [];
      setData(list);
      return list;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load pricing rules';
      setError(msg);
      setData([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * UPDATE Base Price Rule Status (select which source is active)
 */
export function useUpdatePricingRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (ruleId, status) => {
    if (!ruleId) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.put(`${BASE}/${ruleId}`, { status });
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update pricing rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * GET Rounding Rules
 */
export function useRoundingRules() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/rounding`);
      const list = res?.rounding_rules_config ?? [];
      setData(list);
      return list;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load rounding rules';
      setError(msg);
      setData([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * UPDATE Rounding Rule Status (select which strategy is active)
 */
export function useUpdateRoundingRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (ruleId, status) => {
    if (!ruleId) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.put(`${BASE}/rounding/${ruleId}`, { status });
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update rounding rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * GET Pricing Validation (Validity & Activation)
 */
export function usePricingValidation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchValidation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/validation`);
      setData(res);
      return res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load validity settings';
      setError(msg);
      setData(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchValidation();
  }, [fetchValidation]);

  return { data, loading, error, refetch: fetchValidation };
}

/**
 * UPDATE Pricing Validation (Validity & Activation)
 */
export function useUpdatePricingValidation() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { status, start_date, end_date } = payload;
      await axiosInstance.put(`${BASE}/validation`, {
        status,
        start_date: start_date || null,
        end_date: end_date || null,
      });
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update validity settings';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export {
  RULE_NAME_TO_SOURCE,
  SOURCE_TO_RULE_NAME,
  RULE_NAME_TO_STRATEGY,
  STRATEGY_TO_RULE_NAME,
};
